import { useCallback, useEffect, useRef, useState } from 'react';

import useIsCompactViewport from '@/app/hooks/useIsCompactViewport';

const IDLE_TIMEOUT_MS = 4000;

/**
 * Keeps call overlays (header, controls) out of the way of the video: they show
 * on interaction and fade back out once the screen has been left alone.
 *
 * Only on a phone, where the video needs every pixel. A desktop has room for
 * both, and hiding the buttons there cost the AM team a day of thinking the
 * call was broken (CAD-6881).
 */
export default function useOverlayVisibility(idleTimeoutMs: number = IDLE_TIMEOUT_MS) {
  const [isVisible, setIsVisible] = useState(true);
  const isCompact = useIsCompactViewport();
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const reveal = useCallback(() => {
    setIsVisible(true);
    clearTimeout(hideTimer.current);
    if (!isCompact) {
      return;
    }
    hideTimer.current = setTimeout(() => setIsVisible(false), idleTimeoutMs);
  }, [idleTimeoutMs, isCompact]);

  useEffect(function hideAfterInitialIdle() {
    reveal();
    return () => clearTimeout(hideTimer.current);
  }, [reveal]);

  return { isVisible, reveal };
}
